<template>
  <VRow>
    <VCol cols="12">
      <VTabs
        v-model="tab"
        background-color="transparent"
        color="dark"
        slider-size="4"
        slider-color="primary"
        class="account-tabs"
      >
        <VTab class="tab-name mr-4 mr-lg-6">
          {{ $t('account') }}
        </VTab>
        <VTab class="tab-name mr-4 mr-lg-6">
          {{ $t('users') }}
        </VTab>
        <VTab class="tab-name mr-4 mr-lg-6">
          {{ $t('templates') }}
        </VTab>
        <VTab class="title mr-6">
          {{ $t('payment') }}
        </VTab>
        <VTab v-if="tabList.includes(settingsTabs.INTEGRATIONS)" class="tab-name mr-4 mr-lg-6">
          {{ $t('integrations') }}
        </VTab>
      </VTabs>

      <VTabsItems v-model="tab" touchless>
        <VTabItem>
          <Account
            :user="user"
            :key="tabKey"
            :type="addType"
          />
        </VTabItem>
        <VTabItem>
          <Users />
        </VTabItem>
        <VTabItem>
          <NotificationTemplates
            v-if="user"
            :company="user.company"
            :key="tabKey"
            :type="addType"
          />
        </VTabItem>
        <VTabItem>
          <AppWithLoading :fetch="fetch" v-slot:default="{localLoading}">
            <PaymentSettings
              v-if="!localLoading"
              :key="tabKey"
              @input="changeAddType"
              :type="addType"
              :plan-id="planId"
              :plan-title="planTitle"
              :period-id="periodId"
            />
          </AppWithLoading>
        </VTabItem>
        <VTabItem>
          <VacancyIntegrations />
        </VTabItem>
      </VTabsItems>
    </VCol>
  </VRow>
</template>

<script>
import AppWithLoading from '@/components/AppWithLoading';
import useAccount from '@/compositions/accounting/useAccount';
import VacancyIntegrations from '@/components/Integrations/SettingsIntegrations';
import useIntegrations from '@/compositions/useIntegrations';
import Integration from '@/orm/models/Integration/Integration';
import { settingsTabs } from '@/enums';
import {
  ref, toRefs, computed, watch, onMounted,
} from '@vue/composition-api';
import Users from '@/components/Users/Users';
import Account from '@/components/Account/Account.vue';
import NotificationTemplates from '@/components/NotificationTemplates/NotificationTemplates.vue';
import PaymentSettings from '../components/User/PaymentSettings';
import { shouldShowHHIntegration } from '@/helpers/constants';

export default {
  name: 'SettingsPage',
  components: {
    Users,
    Account,
    PaymentSettings,
    NotificationTemplates,
    VacancyIntegrations,
    AppWithLoading,
  },
  metaInfo() {
    return {
      title: this.$t('settings'),
    };
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      default: null,
    },
    addType: {
      type: String,
      default: null,
    },
    planId: {
      type: Number,
      default: null,
    },
    planTitle: {
      type: String,
      default: null,
    },
    periodId: {
      type: Number,
      default: null,
    },
  },
  setup(props, { root }) {
    const { hasIntegrations, fetchAll } = useIntegrations();

    onMounted(() => {
      const storedIntegrations = Integration.all();
      if (!storedIntegrations?.length) {
        fetchAll();
      }
    });

    const { type, addType } = toRefs(props);
    const { fetch } = useAccount();

    const tabList = ref([
      settingsTabs.ACCOUNT,
      settingsTabs.USERS,
      settingsTabs.TEMPLATES,
      settingsTabs.PAYMENT,
    ]);

    watch(hasIntegrations, () => {
      if (hasIntegrations.value && shouldShowHHIntegration() && !tabList.value.includes(settingsTabs.INTEGRATIONS)) {
        tabList.value.push(settingsTabs.INTEGRATIONS);
      } else if (!shouldShowHHIntegration() && tabList.value.includes(settingsTabs.INTEGRATIONS)) {
        const index = tabList.value.indexOf(settingsTabs.INTEGRATIONS);
        if (index > -1) {
          tabList.value.splice(index, 1);
        }
      }
    }, {
      immediate: true,
    });

    const tabKey = ref(Date.now());

    const tab = computed((
      {
        get() {
          return tabList.value.indexOf(type.value) || 0;
        },
        set(index) {
          tabKey.value = Date.now();
          if (index === 3) {
            root.$router.replace({ name: 'settings', params: { type: settingsTabs.PAYMENT } });
          } else {
            root.$router.replace({ name: 'settings', params: { type: tabList.value[index] } });
          }
        },
      }
    ));

    function changeAddType(payload) {
      if (payload !== addType.value && payload && type.value === settingsTabs.PAYMENT) {
        root.$router.replace({ name: 'settings', params: { addType: payload, type: type.value } });
      }
    }

    return {
      tabList,
      tab,
      hasIntegrations,
      settingsTabs,
      changeAddType,
      tabKey,
      fetch,
    };
  },
};
</script>

<style lang="scss" scoped>
.tab-name {
  font-size: 1.25rem;
  letter-spacing: 0.04em;
}

@media #{map-get($display-breakpoints, 'md-and-down')} {
  .tab-name {
    font-size: 1.125rem;
  }
}

</style>

<style lang="scss">
.account-tabs .v-tabs-bar {
  margin-bottom: 0;
}

</style>

<i18n locale="en" lang="yaml">
  integrations: Integrations
  settings: Settings
  account: Account
  templates: E-mail templates
  payment: Payment
  users: Users
</i18n>

<i18n locale="ru" lang="yaml">
  integrations: Интеграции
  settings: Настройки
  account: Аккаунт
  templates: Шаблоны писем
  payment: Оплата
  users: Пользователи
</i18n>
